import React from "react";
import SeoHead from "../components/SeoHead";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import HrefLangTags from "../components/HrefLangTags";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ValuesGrid from "../components/sections/ValuesGrid";
import TeamGrid from "../components/sections/TeamGrid";
import ClientLogos from "../components/sections/ClientLogos";
import StatsBand from "../components/sections/StatsBand";
import CTASection from "../components/sections/CTASection";
import Reveal from "../components/Reveal";
import { useLanguage } from "../contexts/LanguageContext";

export default function AboutPage() {
  const { t, lang } = useLanguage();
  const pageTitle = `${t("about.hero_eyebrow")} · Yastık Medya`;
  const pageDesc = t("about.hero_subtitle");

  return (
    <div data-testid="about-page" className="min-h-screen bg-cream text-ink">
      <SeoHead title={pageTitle} description={pageDesc} path={`/${lang}/about`} imageKey="about" lang={lang} />
      <HrefLangTags pathSuffix="about" />
      <Header />

      <main>
        {/* Hero */}
        <section data-testid="about-hero" className="relative bg-cream pt-12 pb-20 sm:pt-20 sm:pb-section-lg overflow-hidden">
          <div className="pointer-events-none absolute -top-32 end-[-12%] h-[520px] w-[520px] rounded-full bg-gold/15 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 bg-grain opacity-50" />

          <div className="container-page relative grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <motion.span
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="eyebrow"
              >
                <Sparkles className="h-3 w-3 text-gold" />
                {t("about.hero_eyebrow")}
              </motion.span>
              <h1
                className="mt-6 font-display text-display-1 font-semibold text-ink leading-[0.98]"
                style={{ fontVariationSettings: '"opsz" 144' }}
                data-testid="about-hero-title"
              >
                {t("about.hero_title_pre")}{" "}
                <span className="gold-foil-text">{t("about.hero_title_accent")}</span>
              </h1>
              <p className="mt-7 max-w-2xl text-body-lg text-ink/70 leading-relaxed">
                {t("about.hero_subtitle")}
              </p>
            </div>
          </div>
        </section>

        {/* Story */}
        <section data-testid="about-story-section" className="bg-cream py-section sm:py-section-lg border-t border-mist">
          <div className="container-page grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal as="div" className="lg:col-span-5">
              <span className="eyebrow"><span className="h-1 w-1 rounded-full bg-gold" />{t("about.story_eyebrow")}</span>
              <h2 className="mt-5 font-display text-display-2 font-semibold text-ink">
                {t("about.story_title_pre")}{" "}
                <span className="gold-foil-text">{t("about.story_title_accent")}</span>
              </h2>
              <div className="hairline-gold mt-8" />
            </Reveal>
            <div className="lg:col-span-7 space-y-5 text-body-lg text-ink/75 leading-relaxed">
              <p>{t("about.story_p1")}</p>
              <p>{t("about.story_p2")}</p>
              <p>{t("about.story_p3")}</p>
            </div>
          </div>
        </section>

        <ValuesGrid />
        <TeamGrid />
        <ClientLogos />
        <StatsBand />

        <CTASection
          eyebrow={t("about.cta_eyebrow")}
          title={t("about.cta_title_pre")}
          titleAccent={t("about.cta_title_accent")}
          subtitle={t("about.cta_subtitle")}
        />
      </main>
      <Footer />
    </div>
  );
}
